"use client"

import * as React from "react"
import { Link, useLocation } from "react-router-dom"

import {
  ACCOUNT_SETTINGS_RETURN_PATH_KEY,
  ACCOUNT_SETTINGS_TABS,
  accountSettingsTabPath,
  resolveAccountSettingsTabId,
} from "@/constants/accountSettingsSidebar"
import {
  renderRouteIconWithAnimRef,
  sidebarNavIconShellClass,
  type SidebarIconAnimHandle,
  useSidebarNavIconAnimation,
} from "@/components/Sidebar/sidebarNavIconAnimation"
import type { AccountSettingsAnimatedIcon } from "@/constants/accountSettingsSidebar"
import { NavSidebarBackItem } from "@/components/nav-sidebar-back-item"
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useCloseMobileSidebar,
} from "@/components/ui/sidebar"
import { useBillingContext } from "@/contexts/BillingContext"
import { isProfileTabAllowedWithoutTier1 } from "@/utils/billingAllowlist"

const navAccountExpandedButton =
  "group-data-[state=expanded]:h-10 group-data-[state=expanded]:gap-2.5 group-data-[state=expanded]:text-[0.9375rem] group-data-[state=expanded]:leading-snug group-data-[state=expanded]:[&_svg]:size-5"

type AccountSettingsTab = (typeof ACCOUNT_SETTINGS_TABS)[number]

function renderAccountSettingsTabIcon(
  tab: AccountSettingsTab,
  animRef: React.RefObject<SidebarIconAnimHandle | null>
) {
  const AnimatedIcon = tab.icon as AccountSettingsAnimatedIcon
  return renderRouteIconWithAnimRef(
    <AnimatedIcon size={16} color="inherit" className="text-inherit" aria-hidden />,
    animRef
  )
}

function NavAccountSettingsTabItem({
  tab,
  active,
}: {
  tab: AccountSettingsTab
  active: boolean
}) {
  const { iconAnimRef, linkHandlers } = useSidebarNavIconAnimation()
  const closeMobileSidebar = useCloseMobileSidebar()

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        tooltip={tab.label}
        isActive={active}
        className={navAccountExpandedButton}
      >
        <Link
          to={accountSettingsTabPath(tab.id)}
          onClick={closeMobileSidebar}
          {...linkHandlers}
        >
          <span className={sidebarNavIconShellClass}>
            {renderAccountSettingsTabIcon(tab, iconAnimRef)}
          </span>
          <span className="truncate transition-[opacity,margin,max-width] duration-200 ease-linear group-data-[collapsible=icon]:max-w-0 group-data-[collapsible=icon]:opacity-0 group-data-[collapsible=icon]:overflow-hidden">
            {tab.label}
          </span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

function resolveAccountSettingsReturnPath(
  locationState: unknown
): string {
  const statePath = (locationState as { fromSidebarPath?: string } | null)
    ?.fromSidebarPath
  const storedPath = window.sessionStorage.getItem(
    ACCOUNT_SETTINGS_RETURN_PATH_KEY
  )
  return (
    [statePath, storedPath].find(
      (path): path is string => !!path && !path.startsWith("/profile")
    ) ?? "/dashboard"
  )
}

export function NavAccountSettings() {
  const { pathname, search, state: locationState } = useLocation()
  const { isLoading: billingLoading, isTier1Granted } = useBillingContext()
  const activeTabId = resolveAccountSettingsTabId(pathname, search)
  const returnPath = React.useMemo(
    () => resolveAccountSettingsReturnPath(locationState),
    [locationState]
  )

  const visibleTabs = React.useMemo(() => {
    // While billing loads, show the full list to avoid a flash of fewer tabs for subscribers.
    if (billingLoading || isTier1Granted) {
      return ACCOUNT_SETTINGS_TABS
    }
    return ACCOUNT_SETTINGS_TABS.filter((tab) =>
      isProfileTabAllowedWithoutTier1(tab.id)
    )
  }, [billingLoading, isTier1Granted])

  const handleBackNavigate = React.useCallback(() => {
    window.sessionStorage.removeItem(ACCOUNT_SETTINGS_RETURN_PATH_KEY)
  }, [])

  return (
    <SidebarGroup
      className="animate-in fade-in-0 slide-in-from-left-1 duration-200"
      data-testid="shadcn-sidebar-account-settings"
    >
      <SidebarMenu>
        <NavSidebarBackItem
          returnPath={returnPath}
          onNavigate={handleBackNavigate}
        />

        {visibleTabs.map((tab) => (
          <NavAccountSettingsTabItem
            key={tab.id}
            tab={tab}
            active={activeTabId === tab.id}
          />
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}
